import { lazy, Suspense, useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { Hero } from '../components/Hero';
import { analytics } from '../services/analytics';
import { SiteLink as Link } from '../components/SiteLink';
import { APP_STORE_URL, siteUrl } from '../constants/site';

const AppShowcase = lazy(() => import('../components/AppShowcase').then((module) => ({ default: module.AppShowcase })));
const FAQ = lazy(() => import('../components/FAQ').then((module) => ({ default: module.FAQ })));
const Features = lazy(() => import('../components/Features').then((module) => ({ default: module.Features })));
const HowItWorks = lazy(() => import('../components/HowItWorks').then((module) => ({ default: module.HowItWorks })));
const MealPlans = lazy(() => import('../components/MealPlans').then((module) => ({ default: module.MealPlans })));
const NovaCoach = lazy(() => import('../components/NovaCoach').then((module) => ({ default: module.NovaCoach })));
const TrainingRecovery = lazy(() => import('../components/TrainingRecovery').then((module) => ({ default: module.TrainingRecovery })));
const TrustSignals = lazy(() => import('../components/TrustSignals').then((module) => ({ default: module.TrustSignals })));

export const Home = () => {
    const [showDeferredContent, setShowDeferredContent] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        const reveal = () => { timer = window.setTimeout(() => setShowDeferredContent(true), 1500); };
        if (document.readyState === 'complete') reveal();
        else window.addEventListener('load', reveal, { once: true });
        return () => {
            window.removeEventListener('load', reveal);
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, []);

    useSEO({
        title: 'Nurvo: AI Nutrition, Fitness and Recovery Coach',
        description: 'Log meals your way, understand daily patterns and use Nova to turn nutrition, training and recovery context into practical next actions.',
        canonical: siteUrl('/')
    });

    return (
        <>
            <Hero showProductProof={showDeferredContent} />
            {showDeferredContent && <Suspense fallback={null}>
                <NovaCoach />
                <Features />
                <AppShowcase />
                <MealPlans />
                <TrainingRecovery />
                <HowItWorks />
                <TrustSignals />
                <FAQ />
            </Suspense>}
            <section id="download" className="relative overflow-hidden border-t border-white/10 px-4 py-20 sm:px-6 md:py-28">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(190,239,22,0.14),transparent_55%)]" />
                <div className="relative mx-auto max-w-4xl rounded-[2rem] border border-primary/25 bg-surface/80 px-6 py-12 text-center shadow-[0_30px_100px_rgba(0,0,0,0.45)] sm:px-12 md:py-16">
                    <p className="eyebrow justify-center">Available now on iPhone</p>
                    <h2 className="mt-5 text-3xl font-black tracking-[-0.04em] text-white sm:text-5xl">
                        Spend less time logging.<br />Make more of what you learn.
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-text-secondary sm:text-lg">
                        Download Nurvo free on the App Store. Use the core tracking tools for free, or start a 7-day Nurvo Pro trial if you are eligible.
                    </p>
                    <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="button-primary"
                            onClick={() => analytics.appStoreClick('home_closing', 'Download on the App Store')}
                        >
                            Download on the App Store
                        </a>
                        <Link to="/pricing" className="button-secondary" onClick={() => analytics.ctaClick('home_closing', 'Compare Free and Pro')}>
                            Compare Free and Pro
                        </Link>
                    </div>
                    <p className="mt-4 text-xs text-text-muted">Free to download. Optional Pro subscriptions are purchased securely through Apple.</p>
                </div>
            </section>
        </>
    );
};
